import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, Pencil, AlertCircle } from 'lucide-react';
import { showToast } from '@/services/toast.service';
import TenantConfigService, { SnowflakeWarehouse } from '@/services/tenantConfig.service';
import {
  computeWarehouseChanges,
  formatAutoSuspend,
  matchSizeOption,
} from './warehouseForm';

interface WarehousesCardProps {
  tenantConfigId: string;
}

function warehouseStateLabel(state?: string): string {
  switch (state) {
    case 'STARTED':
      return 'Running';
    case 'RESUMING':
      return 'Resuming';
    case 'RESIZING':
      return 'Resizing';
    case 'SUSPENDED':
      return 'Suspended';
    default:
      return state ?? 'Unknown';
  }
}

export default function WarehousesCard({ tenantConfigId }: WarehousesCardProps) {
  const [editing, setEditing] = useState<SnowflakeWarehouse | null>(null);
  const [size, setSize] = useState('');
  const [autoSuspend, setAutoSuspend] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['snowflakeWarehouses', tenantConfigId],
    queryFn: () => TenantConfigService.getInstance().getSnowflakeWarehouses(tenantConfigId),
    enabled: !!tenantConfigId,
    staleTime: 30 * 1000,
    retry: false,
  });

  // Memoized because it feeds the effect + memo below; a fresh [] each render
  // would re-seed the form under the user mid-edit.
  const sizes = useMemo(() => data?.sizes ?? [], [data]);

  // `data` is a new object after every refetch (react-query refetches on window
  // focus once staleTime has passed), so the effect below reads `sizes` through a
  // ref rather than depending on it: otherwise coming back from another tab would
  // re-seed the form and wipe what the user had typed.
  const sizesRef = useRef(sizes);
  sizesRef.current = sizes;

  // Seed the form once per row opened for editing, from the snapshot the user was
  // shown (`editing`), which the diff below also compares against. Snowflake reports
  // the label form ("2X-Large"); the Select is keyed on the ALTER token, so match across.
  useEffect(() => {
    if (!editing) return;
    setSize(matchSizeOption(sizesRef.current, editing.size)?.value ?? '');
    setAutoSuspend(editing.auto_suspend != null ? String(editing.auto_suspend) : '');
    setSaveError('');
  }, [editing]);

  const changes = useMemo(
    () =>
      editing
        ? computeWarehouseChanges(editing, sizes, { size, autoSuspend })
        : {},
    [editing, size, autoSuspend, sizes]
  );

  const isDirty = Object.keys(changes).length > 0;

  const save = useCallback(async () => {
    if (!editing || !isDirty) return;
    setSaving(true);
    setSaveError('');
    try {
      await TenantConfigService.getInstance().updateSnowflakeWarehouse(
        tenantConfigId,
        editing.key,
        changes
      );
      showToast.success(`${editing.label} warehouse updated`);
      setEditing(null);
      refetch();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setSaving(false);
    }
  }, [editing, changes, isDirty, tenantConfigId, refetch]);

  if (!tenantConfigId) return null;

  return (
    <>
      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit {editing?.label} Warehouse</DialogTitle>
            <DialogDescription>
              {editing?.name} — changes apply immediately in Snowflake. Queries already
              running finish on the current size.
            </DialogDescription>
          </DialogHeader>

          <DialogBody className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="warehouse_size">Size</Label>
              <Select value={size} onValueChange={setSize}>
                <SelectTrigger id="warehouse_size">
                  <SelectValue placeholder="Select a size" />
                </SelectTrigger>
                <SelectContent>
                  {sizes.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                Each step up doubles both the compute and the credits burned per second.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="auto_suspend">Auto-suspend (seconds)</Label>
              <Input
                id="auto_suspend"
                type="number"
                min={data?.min_auto_suspend}
                max={data?.max_auto_suspend}
                value={autoSuspend}
                onChange={(e) => setAutoSuspend(e.target.value)}
              />
              <p className="text-sm text-muted-foreground">
                How long the warehouse sits idle before shutting down. Lower spends less;
                too low makes short gaps between queries pay the resume cost repeatedly.
              </p>
            </div>

            {saveError && (
              <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-950 rounded-md">
                <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-800 dark:text-red-200">
                    Couldn't update the warehouse
                  </p>
                  <p className="text-sm text-red-600 dark:text-red-400 mt-1 font-mono">
                    {saveError}
                  </p>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={save} disabled={!isDirty || saving}>
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </div>
          </DialogBody>
        </DialogContent>
      </Dialog>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Warehouses</CardTitle>
          <CardDescription>
            The compute Pliable runs on in your Snowflake account. Sizing and auto-suspend
            are the two levers on what these cost.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-950 rounded-md">
              <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800 dark:text-red-200">
                We couldn't read your warehouses from Snowflake
                {error instanceof Error && error.message ? `: ${error.message}` : '.'} Validate
                the connection above and try again.
              </p>
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-3 font-medium">Warehouse</th>
                    <th className="text-left p-3 font-medium">Size</th>
                    <th className="text-left p-3 font-medium">Auto-suspend</th>
                    <th className="text-left p-3 font-medium">State</th>
                    <th className="p-3 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.warehouses ?? []).map((wh) => (
                    <tr key={wh.key} className="border-b last:border-0">
                      <td className="p-3">
                        <div className="font-medium">{wh.label}</div>
                        <div className="text-muted-foreground text-xs font-mono">{wh.name}</div>
                        <div className="text-muted-foreground text-xs mt-1">
                          {wh.description}
                        </div>
                      </td>
                      <td className="p-3">{wh.exists ? wh.size : '—'}</td>
                      <td className="p-3">
                        {wh.exists ? (
                          <>
                            {formatAutoSuspend(wh.auto_suspend)}
                            {wh.auto_resume === false && (
                              <Badge variant="outline" className="ml-2 text-xs">
                                No auto-resume
                              </Badge>
                            )}
                          </>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="p-3">
                        {wh.exists ? (
                          <div className="flex items-center gap-2">
                            <Badge variant={wh.state === 'STARTED' ? 'default' : 'outline'}>
                              {warehouseStateLabel(wh.state)}
                            </Badge>
                            {!!wh.running && (
                              <span className="text-muted-foreground text-xs">
                                {wh.running} running
                              </span>
                            )}
                          </div>
                        ) : (
                          <Badge variant="outline" className="text-amber-700 border-amber-300 dark:text-amber-400 dark:border-amber-700">
                            Not found
                          </Badge>
                        )}
                      </td>
                      <td className="p-3">
                        <Button
                          variant="ghost"
                          size="icon"
                          disabled={!wh.exists}
                          onClick={() => setEditing(wh)}
                          title={
                            wh.exists
                              ? `Edit ${wh.label.toLowerCase()} warehouse`
                              : "Pliable can't see this warehouse in Snowflake"
                          }
                        >
                          <Pencil className="h-4 w-4 text-muted-foreground" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}
